import { parseUnits } from 'ethers';
import { Erc20Token__factory, PearlWallet__factory } from 'public/abi/evm/types';
import { getCancelErrorMessage, modifyHistoryHashStatus, saveHashToLocalStorage } from '~/utils';
import { ACTION_REJECTED } from '~/utils/constants';
import { BigNumber } from 'bignumber.js';
import { useWalletInfo, useWeb3ModalAccount, useWeb3ModalProvider } from '@web3modal/ethers/vue';
import { useI18n, useMainStore, useNuxtApp } from '#imports';

export default () => {
  const isLoading = ref(false);
  const showExchangePopup = ref(false);
  const { walletInfo } = useWalletInfo();
  const { address } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();
  const { blockchain } = useEnvs();
  const store = useMainStore();
  const { $errorHandler } = useNuxtApp();
  const { t } = useI18n();
  const { getSigner } = useProvider();

  async function callExchangeMethod(quantity: number, tokenName: string) {
    isLoading.value = true;

    try {
      const signer = await getSigner();
      const pearlWalletContract = PearlWallet__factory.connect(blockchain.contracts.pearlWallet, signer);
      const tokenQuantity = parseUnits(quantity.toString(), 'ether');

      const tx = await pearlWalletContract.sellPearls(tokenQuantity);
      saveHashToLocalStorage(`${t('appStatisticExchangeButton')} ${quantity} ${tokenName}`, tx.hash);
      store.updateVersion();

      await tx.wait();

      modifyHistoryHashStatus(tx.hash, 'Done');
    } catch (error: unknown) {
      if (walletInfo.value?.name) {
        const errorMessage = getCancelErrorMessage(error, walletInfo.value.name);
        $errorHandler(t('exchangeFailedError') + ' ' + t(errorMessage));
      }
    } finally {
      isLoading.value = false;
      showExchangePopup.value = false;
    }
  }

  async function exchange(quantity: number, tokenName: string) {
    if (!walletProvider.value) {
      return;
    }

    try {
      const tokenQuantity = parseUnits(quantity.toString(), 'ether');
      const signer = await getSigner();
      const pearlContract = Erc20Token__factory.connect(blockchain.contracts.pearl.addresses.contract, signer);
      const allowance = await pearlContract.allowance(address.value as string, blockchain.contracts.pearlWallet);

      if (new BigNumber(allowance.toString()).isLessThan(Number(tokenQuantity))) {
        const tx = await pearlContract.approve(blockchain.contracts.pearlWallet, tokenQuantity);
        saveHashToLocalStorage(t('notificationApprove'), tx.hash);

        store.updateVersion();
        await tx.wait();

        modifyHistoryHashStatus(tx.hash, 'Done');
        store.updateVersion();
        await callExchangeMethod(quantity, tokenName);
        return;
      }

      await callExchangeMethod(quantity, tokenName);
    } catch (error) {
      if (walletInfo.value?.name) {
        const errorMessage = getCancelErrorMessage(error, walletInfo.value.name);

        const message = t('exchangeFailedError') + ' ' + t(errorMessage);
        $errorHandler(message);

        throw new Error(ACTION_REJECTED);
      }
    } finally {
      isLoading.value = false;
      showExchangePopup.value = false;
    }
  }

  return {
    isLoading,
    showExchangePopup,
    exchange
  };
};
